import Slot, {MoveBehaviour} from '../domain/Slot';
import {getFuncBatched} from '../utils/batch-util';
import * as nodeUtils from '../utils/node';
import * as perf from '../utils/performance';
import pubsub from '../utils/pubsub';
import * as adManager from './adManagerService';
import breakpoints from './breakpoints';
import lazyLoadService from './lazyLoadService';
import sizeMappings, {SizeMapping} from './sizeMappings';
import {getSlots, getSlotsAndPositions} from './slotManager';
import logger from '../utils/logger';


let slots: Array<Slot> = [],
	waitingForDefine: Record<Slot['name'], AnyFunction> = {};

_hookEvents();

export async function init() {
	perf.mark('xandr - slotloader.init - start');

	waitingForDefine = {};

	slots.forEach((slot) => adManager.removeSlot(slot));

	slots = getSlots();

	await defineSlots(slots.filter((slot) => slot.getPreload()));

	perf.mark('xandr - slotloader.init - end');
}

function _hookEvents() {
	pubsub.subscribe('persisted', _onPersisted);
	pubsub.subscribe('slot.defined', _onSlotDefined);
}

function _onPersisted() {
	const slotsToRefresh = slots.filter((/** Slot*/ slot) => slot.state.showTagCalled);

	adManager.updatePageTargeting();

	return refreshSlots(slotsToRefresh, 'persisted');
}

function _isUsedOnCurrentBreakpoint(slot: Slot) {
	const sizes = sizeMappings.getSizesFromSizeMapForBreakpoint(slot.sizeMapping, breakpoints.getCurrentBreakpoint());

	return typeof sizes !== 'undefined' && sizes.length > 0;
}

export async function defineSlots(toDefine: Array<Slot>) {
	perf.mark('xandr - slotmanager.define - start');

	const slotsToDefine = toDefine
		.filter(_isUsedOnCurrentBreakpoint);

	slotsToDefine.forEach((slot) => {
		adManager.defineSlot(slot);
	});

	perf.mark('xandr - slotmanager.extendSlots - start');
	await pubsub.publish('extendSlots', slotsToDefine);
	perf.mark('xandr - slotmanager.extendSlots - end');

	adManager.loadSlots(slotsToDefine);

	perf.mark('xandr - slotmanager.define - end');
}

const defineSlotsBatched = getFuncBatched<[Array<Slot>]>((calls) => {
	return defineSlots(calls.flatMap(c => c[0]));
});

async function _waitForSlotDefine(slot: Slot) {
	if (slot.state.defined) {
		return;
	}

	// It should define a non-preloaded slot, but no others as those are likely broken or busy.
	if (slot.getPreload()) {
		await new Promise((resolve) => {
			waitingForDefine[slot.name] = resolve;
		});
	} else {
		await defineSlotsBatched([slot]);
	}
}

function _onSlotDefined(slot: Slot) {
	if (typeof waitingForDefine[slot.name] === 'function') {
		waitingForDefine[slot.name]();
		delete waitingForDefine[slot.name];
	}
}

export async function refreshSlots(slotsToRefresh: Array<Slot>, reason?: string) {
	slotsToRefresh.forEach((slot) => {
		slot.targeting.refresh = reason;

		slot.setError(null);
		slot.setMediated(false);
		adManager.resetSlot(slot);

		if (slot.refreshSettings?.fixedHeight) {
			filterSizesToSlotSize(slot);
		}
	});

	perf.mark('xandr - slotmanager.extendSlots - refresh - start');
	await pubsub.publish('extendSlots', slotsToRefresh);
	perf.mark('xandr - slotmanager.extendSlots - refresh - end');

	adManager.refreshSlots(slotsToRefresh);
}

function filterSizesToSlotSize(slot: Slot) {
	if (slot.isEmpty || !slot.adResponse?.height) {
		return;
	}

	const filteredSizeMapping: SizeMapping = {};

	Object.entries(sizeMappings.getBreakpointSizeMapping(slot.sizeMapping)).forEach(([bp, sizes]) => {
		filteredSizeMapping[bp] = sizes?.filter((size) => size[1] === slot.adResponse.height);
	});

	filteredSizeMapping.customBreakpointMapping = sizeMappings.getCustomBreakpointMapping(slot.sizeMapping)?.map(s => ({
		...s,
		sizes: s.sizes.filter((size) => size[1] === slot.adResponse.height)
	}));

	adManager.applySizeMapping(slot, filteredSizeMapping);
}

export async function loadSlotInNode(slot: Slot) {
	await _waitForSlotDefine(slot);
	await lazyLoadService.waitForSlot(slot);

	if (!nodeUtils.isConnected(slot.node)) {
		logger.warn('Tried rendering slot in a node that\'s no longer connected to the DOM', slot, slot.node);

		return;
	}

	// This isn't ad server specific, but thanks to the order of operations, it does not cause issues as far as we can tell.
	if (slot.state.showTagCalled) {
		await refreshSlots([slot], MoveBehaviour.MOVE);
	} else {
		await adManager.renderSlot(slot);
	}

	return slot;
}

export async function loadSlot(platformName: string, nodeId: string | HTMLElement = platformName): Promise<Array<Slot>> {
	const positions = await getSlotsAndPositions(platformName, nodeId),
		loadedSlots = await Promise.all(
			positions.map((slot) => loadSlotInNode(slot))
		);

	return loadedSlots.filter(s => s);
}
